import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { Alert, Badge, Card, CardBody, Col, Container, Row, Spinner, Table } from "reactstrap";

import { apiFetch, API } from "../../services/api";
import { readError } from "../../helpers/whatsappTemplates";
import { comparableCost, delta, eur, pct, ENTRY_LABELS, FAMILY_LABELS, PLACEMENT_LABELS } from "../../helpers/whatsappStats";

const kpi = { background: "#fff", borderRadius: 14, padding: "14px 16px", boxShadow: "0 0 0 1px rgba(0,0,0,.05)", height: "100%" };
const PERIODS = [7, 30, 90];
const STORE = "waStatsDays";

function readDays() {
    try { const v = Number(localStorage.getItem(STORE)); return PERIODS.includes(v) ? v : 30; } catch (e) { return 30; }
}

function Tile({ label, value, pair, sub, inverse }) {
    const d = delta(pair);
    const good = d.up === null ? null : inverse ? !d.up : d.up;
    return (
        <div style={kpi}>
            <div className="small text-muted">{label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.4 }}>{value}</div>
            <div className="small">
                <span style={{ color: good === null ? "#888" : good ? "#1a7f37" : "#c62828", fontWeight: 700 }}>{d.label}</span>
                {sub ? <span className="text-muted"> · {sub}</span> : null}
            </div>
        </div>
    );
}

function Unavailable({ what }) {
    return <div style={kpi}><div className="small text-muted">{what}</div><div className="small text-muted mt-2">Données indisponibles</div></div>;
}

function Bars({ title, data, labels }) {
    const entries = Object.entries(data || {}).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
    const total = entries.reduce((s, [, n]) => s + n, 0);
    return (
        <Card className="h-100"><CardBody>
            <h6 className="mb-3">{title}</h6>
            {entries.length === 0 ? <div className="small text-muted">Aucune donnée sur la période</div> : entries.map(([k, n]) => (
                <div key={k} className="mb-2">
                    <div className="d-flex justify-content-between small"><span>{labels[k] || k || "Autre"}</span><span>{n} · {pct(n, total)}</span></div>
                    <div style={{ background: "#eee", borderRadius: 4, height: 6 }}><div style={{ width: pct(n, total), background: "#111", height: 6, borderRadius: 4 }} /></div>
                </div>
            ))}
        </CardBody></Card>
    );
}

// Stats du canal WhatsApp : business, croissance, santé, comparées à la période précédente.
export function Stats() {
    const [days, setDays] = useState(readDays);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        try { localStorage.setItem(STORE, String(days)); } catch (e) { /* stockage indisponible */ }
        setLoading(true);
        apiFetch(API.back + "/whatsapp-stats?days=" + days, { market: false })
            .then(async (res) => { if (!res.ok) throw new Error(await readError(res)); return res.json(); })
            .then((d) => { setData(d); setError(null); })
            .catch((e) => setError("Stats indisponibles : " + e.message))
            .finally(() => setLoading(false));
    }, [days]);

    const header = (
        <div className="d-flex align-items-center mb-3">
            <h1 className="h3 mb-0 mr-3">Stats WhatsApp</h1>
            {PERIODS.map((p) => (
                <Badge key={p} pill color={p === days ? "dark" : "light"} className="mr-2" style={{ cursor: "pointer", fontSize: 13, padding: "6px 12px" }} onClick={() => setDays(p)}>{p} jours</Badge>
            ))}
            {loading ? <Spinner size="sm" className="ml-2" /> : null}
            {data ? <span className="ml-auto small text-muted">du {data.from.split("-").reverse().join("/")} au {data.to.split("-").reverse().join("/")}, comparé aux {days} jours précédents</span> : null}
        </div>
    );
    if (error) return <Container fluid className="p-0">{header}<Alert color="danger">{error}</Alert></Container>;
    if (!data) return <Container fluid className="p-0">{header}</Container>;

    const { business: b, growth: g, health: h } = data;
    const pair = (p, f) => (p ? { current: f(p.current), previous: f(p.previous) } : null);
    const costOf = (c) => c.claude + c.meta;
    // unsubscribes (un STOP compté une fois) ; repli sur optOuts tant que le back_api n'est pas redéployé
    const unsub = (m) => m.unsubscribes || m.optOuts;
    const metaNote = b.cost && b.cost.current.metaPartial ? "Meta suivi depuis le 13/10" : "estimation";
    const chart = g ? {
        series: [{ name: "Nouveaux contacts", data: g.newContactsByDay.map((d) => d.count) }],
        options: {
            chart: { toolbar: { show: false } }, colors: ["#111"], plotOptions: { bar: { columnWidth: "60%", borderRadius: 3 } }, dataLabels: { enabled: false },
            xaxis: { categories: g.newContactsByDay.map((d) => d.date.slice(8, 10) + "/" + d.date.slice(5, 7)) }, yaxis: { labels: { formatter: (v) => Math.round(v) } },
        },
    } : null;

    return (
        <Container fluid className="p-0">
            {header}
            {data.warnings.length ? <Alert color="warning">{data.warnings.join(" · ")}</Alert> : null}

            <h5 className="mt-2 mb-2">Business</h5>
            <Row className="mb-3">
                <Col md="6" lg="3" className="mb-2">{b.clicks ? <Tile label="Clics vers les marchands" value={b.clicks.current.total} pair={pair(b.clicks, (c) => c.total)}
                    sub={Object.keys(FAMILY_LABELS).map((k) => `${FAMILY_LABELS[k]} ${b.clicks.current.byFamily[k] || 0}`).join(" · ")} /> : <Unavailable what="Clics vers les marchands" />}</Col>
                <Col md="6" lg="3" className="mb-2">{b.sales ? <Tile label="Ventes · commissions" value={`${b.sales.current.count} · ${eur(b.sales.current.commission)}`} pair={pair(b.sales, (s) => s.commission)}
                    sub={`dont campagnes ${b.sales.current.campaignCount}`} /> : <Unavailable what="Ventes" />}</Col>
                <Col md="6" lg="3" className="mb-2">{b.cost ? <Tile label="Coût (Claude + Meta)" value={eur(costOf(b.cost.current))} pair={comparableCost(b.cost) ? pair(b.cost, costOf) : null} inverse
                    sub={`Claude ${eur(b.cost.current.claude)} · Meta ${eur(b.cost.current.meta)} · ${metaNote}`} /> : <Unavailable what="Coût" />}</Col>
                <Col md="6" lg="3" className="mb-2">{b.margin ? <Tile label="Marge" value={eur(b.margin.current)} pair={comparableCost(b.cost) ? b.margin : null} sub={b.cost && b.cost.current.metaPartial ? "commissions − coûts · " + metaNote : "commissions − coûts"} /> : <Unavailable what="Marge" />}</Col>
            </Row>

            <h5 className="mb-2">Croissance</h5>
            {g ? (<>
                <Row className="mb-3">
                    <Col md="6" lg="3" className="mb-2"><Tile label="Nouveaux contacts" value={g.newContacts.current} pair={g.newContacts} /></Col>
                    <Col md="6" lg="3" className="mb-2"><Tile label="Inscrits aux bons plans" value={g.marketing.activeOptIns} pair={null} sub={`+${g.marketing.newOptIns.current} sur la période (${g.marketing.newOptIns.previous} avant · ${delta(g.marketing.newOptIns).label})`} /></Col>
                    <Col md="6" lg="3" className="mb-2"><Tile label="Désinscriptions" value={unsub(g.marketing).current} pair={unsub(g.marketing)} inverse
                        sub={`dont STOP ${g.marketing.stops.current}`} /></Col>
                    <Col md="6" lg="3" className="mb-2">{g.linkedAccounts ? <Tile label="Comptes reliés" value={g.linkedAccounts.total} pair={g.linkedAccounts.new} sub={`+${g.linkedAccounts.new.current} sur la période`} /> : <Unavailable what="Comptes reliés" />}</Col>
                </Row>
                <Row className="mb-3">
                    <Col lg="6" className="mb-2"><Card className="h-100"><CardBody><h6>Nouveaux contacts par jour</h6><Chart options={chart.options} series={chart.series} type="bar" height={240} /></CardBody></Card></Col>
                    <Col lg="3" className="mb-2"><Bars title="Points d'entrée (nouveaux contacts)" data={g.entryPoints} labels={ENTRY_LABELS} /></Col>
                    <Col lg="3" className="mb-2">{g.siteButtonClicks ? <Bars title={`Clics sur les boutons du site (${g.siteButtonClicks.current.total}, ${delta(pair(g.siteButtonClicks, (c) => c.total)).label})`}
                        data={g.siteButtonClicks.current.byPlacement} labels={PLACEMENT_LABELS} /> : <Unavailable what="Clics sur les boutons du site" />}</Col>
                </Row>
            </>) : <Row className="mb-3"><Col><Unavailable what="Croissance" /></Col></Row>}

            <h5 className="mb-2">Santé</h5>
            <Row className="mb-3">
                <Col md="6" lg="3" className="mb-2">{h.conversations ? <Tile label="Conversations" value={h.conversations.current.turns} pair={pair(h.conversations, (c) => c.turns)}
                    sub={`${h.conversations.current.contacts} contacts actifs`} /> : <Unavailable what="Conversations" />}</Col>
                <Col md="6" lg="3" className="mb-2">{h.latencyMs ? <Tile label="Temps de réponse médian" value={(h.latencyMs.current.p50 / 1000).toFixed(1).replace(".", ",") + " s"} pair={pair(h.latencyMs, (l) => l.p50)} inverse
                    sub={`p90 ${(h.latencyMs.current.p90 / 1000).toFixed(1).replace(".", ",")} s`} /> : <Unavailable what="Temps de réponse" />}</Col>
                <Col md="6" lg="3" className="mb-2">{h.alerts ? <Tile label="Alertes créées" value={h.alerts.price.created.current + h.alerts.size.created.current}
                    pair={{ current: h.alerts.price.created.current + h.alerts.size.created.current, previous: h.alerts.price.created.previous + h.alerts.size.created.previous }}
                    sub={`déclenchées ${h.alerts.price.triggered.current + h.alerts.size.triggered.current} · ouvertes ${h.alerts.price.open + h.alerts.size.open}`} /> : <Unavailable what="Alertes" />}</Col>
                <Col md="6" lg="3" className="mb-2">{h.dlq !== null ? <div style={kpi}><div className="small text-muted">Messages bloqués (DLQ)</div>
                    <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.4, color: h.dlq > 0 ? "#c62828" : undefined }}>{h.dlq}</div><div className="small text-muted">en ce moment</div></div> : <Unavailable what="Messages bloqués" />}</Col>
            </Row>
            {h.delivery ? (
                <Card><CardBody>
                    <h6>Remise des messages envoyés</h6>
                    <Row className="mb-2">
                        {[["Lus", "read"], ["Remis", "delivered"], ["Envoyés", "sent"], ["Échecs", "failed"]].map(([l, k]) => {
                            const c = h.delivery.current; const total = c.read + c.delivered + c.sent + c.failed;
                            return <Col key={k} xs="6" md="3"><div className="small text-muted">{l}</div><div style={{ fontSize: 20, fontWeight: 800 }}>{c[k]} <span className="small text-muted">{pct(c[k], total)}</span></div></Col>;
                        })}
                    </Row>
                    {h.delivery.current.topErrors.length ? (
                        <Table size="sm" className="mb-0"><thead><tr><th>Code</th><th>Motif</th><th className="text-right">Messages</th></tr></thead>
                            <tbody>{h.delivery.current.topErrors.map((e) => <tr key={e.code}><td>{e.code}</td><td>{e.label}</td><td className="text-right">{e.count}</td></tr>)}</tbody></Table>
                    ) : <div className="small text-muted">Aucun échec sur la période</div>}
                </CardBody></Card>
            ) : <Unavailable what="Remise des messages" />}
        </Container>
    );
}
